import React from 'react';
import {useHistory} from 'react-router-dom';
import {Box} from '@material-ui/core';

import {Utils} from 'commons';
import {useSnackbar} from 'notistack';
import {useFlowFieldSearch, useSpinner} from 'hooks';
import {EfeAccordion, EfeBulletList, EfeLink} from 'components';
import {ClientService, DocumentServices} from 'services';

import {
    ATTR_NULL,
    Constants,
    HistoryRoutes,
    Messages,
    PersonTypes,
    Roles,
    SnackbarVariants,
    ViewKeys,
} from 'commons/constants';
import {
    Bullet,
    EfeAccordionValues,
    EfeResponse,
    Field,
    RealOwnerServiceRequest,
    View,
} from 'commons/models';
import {MainContent} from 'layouts/commons';
import {data, guidelines} from 'commons/defaults/real-owner';
import {useFlowContext} from 'context/onboarding-flow/OnboardingFlow';
import {useMenuContext} from 'context/onboarding-menu/OnboardingMenu';
import {nextStep} from 'commons/utils/Functions';
import {ReactComponent as ActionIcon} from 'assets/icons/section/action/edit.svg';
import {useRelatedPeopleContext} from 'context/related-people-flow/RelatedPeopleFlow';
import RealOwnerPersonalInfoForm from './real-owner-personal-info-form/RealOwnerPersonalInfoForm';
import {fieldCatalogGetter} from 'hooks/FlowHook';
import {RealOwnerInfo} from 'commons/types/Types';

function RealOwnerPersonalInfo() {
    const history = useHistory();
    const {enqueueSnackbar} = useSnackbar();
    const {flow, updateModuleStep} = useFlowContext();
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [auxRealownerInfo, setAuxRealOwnerInfo] = React.useState<RealOwnerServiceRequest>();
    const [viewFields] = React.useState<Array<Field>>(useFlowFieldSearch());
    const [fields, setFields] = React.useState<Array<Field>>([]);
    const {menu, expedientId, fatherKycId, kycId, setKycId} = useMenuContext();
    const {setEditLocation, setTempRealOwner, setTempRealOwnerActive, realOwners, setRealOwners} =
        useRelatedPeopleContext();

    const bullets: Array<Bullet> = data;
    const guideLinesRealOwner: Array<Bullet> = guidelines;

    React.useEffect(() => {
        if (!fields || (fields && fields.length <= 0)) {
            setFields(viewFields);
        }
    }, []);

    React.useEffect(() => {
        if (fatherKycId > 0 && kycId > 0) {
            (async function () {
                try {
                    setIsLoading(true);
                    const response: EfeResponse<RealOwnerServiceRequest> =
                        await ClientService.getRealOwnerData(kycId);
                    if (response && response.data) {
                        const realOwner = response.data;
                        setAuxRealOwnerInfo(response.data);
                        setTempRealOwner({
                            kycId: response.data.kycId ? response.data.kycId : 0,
                            completeName: `${realOwner.name} ${realOwner.lastName} ${realOwner.secondLastName}`,
                            foreignCitizen: realOwner.foreignCitizen,
                        });
                    }
                    setIsLoading(false);
                } catch (error) {
                    setIsLoading(false);
                    setAuxRealOwnerInfo(undefined);
                }
            })();
        }
    }, [kycId]);

    async function overrideFields(view: View | undefined) {
        if (view && view.fields) {
            await setFields(view.fields);
        } else {
            await setFields(viewFields);
        }
    }

    async function updateRealOwnerList(data: RealOwnerInfo) {
        const aux: Array<RealOwnerInfo> = [...realOwners];
        setTempRealOwner(data);
        const index = aux.findIndex((item) => item.kycId === data.kycId);
        if (index >= 0) {
            aux[index] = {...data};
            setRealOwners([...aux]);
        } else {
            setRealOwners([...aux, data]);
        }
    }

    async function onSubmit(values: RealOwnerServiceRequest) {
        const request: RealOwnerServiceRequest = Utils.functions.clearUnusedFields(
            {...values},
            fields
        );
        try {
            setIsLoading(true);
            request.viewId = flow.data.viewId;
            request.expedientId = expedientId;
            request.personTypeKey = PersonTypes.FISICA;
            request.fatherKycId = fatherKycId;
            request.rol = Roles.REAL_OWNER;
            request.viewFatherId = flow.data.fatherId;
            if (kycId >= 0) {
                request.kycId = kycId;
            }
            const response: EfeResponse<RealOwnerServiceRequest> =
                await ClientService.saveRealOwner(request);
            if (response && response.data) {
                updateRealOwnerList({
                    kycId: response.data.kycId ? response.data.kycId : 0,
                    completeName: `${request.name} ${request.lastName} ${request.secondLastName}`,
                    foreignCitizen: request.foreignCitizen,
                });
                setKycId(response.data.kycId ? response.data.kycId : 0);
                enqueueSnackbar(Messages.INFORMATION_SAVED_SUCCESSFULLY, {
                    variant: SnackbarVariants.SUCCESS,
                });
                setTempRealOwnerActive(true);
                const path = nextStep(flow, menu);
                if (Constants.MODULE_STEP !== path) {
                    setIsLoading(false);
                    history.push(path);
                } else {
                    updateModuleStep(true);
                    flow.data.expandKey = path;
                    setIsLoading(false);
                }
            } else {
                setFields([...fields]);
                setIsLoading(false);
            }
        } catch (error) {
            setIsLoading(false);
            setFields([...fields]);
            enqueueSnackbar(Messages.ERROR_SAVING_INFORMATION, {
                variant: SnackbarVariants.ERROR,
            });
        }
    }

    const accordion: EfeAccordionValues = {
        icon: <ActionIcon />,
        title: 'Informaci\u00F3n personal del Propietario Real',
        reactElement: (
            <Box mt={8}>
                {!isLoading && (
                    <RealOwnerPersonalInfoForm
                        submitHandler={onSubmit}
                        viewId={flow.data.viewId}
                        fieldHandler={overrideFields}
                        viewKey={flow.data.id}
                        auxRealownerInfo={auxRealownerInfo}
                        fields={[
                            ...fieldCatalogGetter(
                                fields.filter((field) => ATTR_NULL !== field.attrName)
                            ),
                        ].sort((a: Field, b: Field) => (a.idOrder < b.idOrder ? -1 : 1))}
                    />
                )}
            </Box>
        ),
        expandIcon: true,
        disabled: false,
        expanded: ViewKeys.REAL_OWNER_PERSONAL_INFO === flow.data.expandKey,
    };

    async function onDownload(doc: string) {
        try {
            setIsLoading(true);
            const response = await DocumentServices.getDocument(doc);
            Utils.functions.downloadDocs(response, `${doc}.pdf`);
            enqueueSnackbar(Messages.FILE_DOWNLOAD_OK, {
                variant: SnackbarVariants.SUCCESS,
            });
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            enqueueSnackbar(Messages.FILE_DOWNLOAD_ERROR, {
                variant: SnackbarVariants.ERROR,
            });
        }
    }

    function onChangeHandler() {
        setTempRealOwnerActive(true);
        setEditLocation(false);
        history.push(HistoryRoutes.REAL_OWNER_PERSONAL_INFO);
    }

    return (
        <MainContent>
            {isLoading && useSpinner(isLoading)}
            <Box mb={5}>
                <EfeBulletList bullets={bullets} handleHeaderAction={onDownload} />
                {/* <EfeBulletList bullets={realOwnerControl} handleHeaderAction={onDownload} /> */}
                <EfeBulletList bullets={guideLinesRealOwner} handleHeaderAction={onDownload} />
                <EfeLink
                    variant='body2'
                    color='secondary'
                    underline='always'
                    bold={true}
                    content={
                        'Fuente: Lineamientos para la Identificaci\u00F3n del Propietario Real'
                    }
                    onClick={() => {
                        window.open(
                            'https://www.gob.mx/cnbv/acciones-y-programas/lineamientos-para-la-identificacion-del-propietario-real-119323'
                        );
                    }}
                />
            </Box>
            <EfeAccordion onChange={onChangeHandler} values={[accordion]} />
        </MainContent>
    );
}

export default RealOwnerPersonalInfo;
